import { WorkflowNode } from '../types';

export const trizWorkflowNodes: WorkflowNode[] = [
  // Phase 1: Problem Identification & Definition
  {
    id: 'problem-identification',
    name: 'Problem Identification',
    description: 'Identify and clearly define the specific problem that needs solving',
    type: 'start',
    position: { x: 100, y: 120 },
    connections: ['problem-analysis'],
    status: 'available',
    details: 'Define the initial problem statement, gather stakeholder input, and establish problem boundaries. This is the foundation of systematic innovation.',
    nextSteps: ['Document problem context', 'Identify stakeholders', 'Set success criteria']
  },
  {
    id: 'problem-analysis',
    name: 'Problem Analysis',
    description: 'Analyze the problem structure and identify key contradictions',
    type: 'process',
    position: { x: 320, y: 120 },
    connections: ['contradiction-mapping'],
    status: 'locked',
    details: 'Break down the problem into its core components, identify system boundaries, and understand the current situation vs desired outcome.',
    nextSteps: ['System analysis', 'Function analysis', 'Root cause identification']
  },
  {
    id: 'contradiction-mapping',
    name: 'Contradiction Mapping',
    description: 'Map technical and physical contradictions within the system',
    type: 'process',
    position: { x: 540, y: 120 },
    connections: ['triz-tool-selection'],
    status: 'locked',
    details: 'Identify technical contradictions (improving one parameter leads to degrading another) and physical contradictions (conflicting requirements for the same object).',
    nextSteps: ['Technical contradiction analysis', 'Physical contradiction analysis', 'Administrative contradiction review']
  },

  // Phase 2: TRIZ Tool Selection & Application
  {
    id: 'triz-tool-selection',
    name: 'TRIZ Tool Selection',
    description: 'Select appropriate TRIZ tools based on problem characteristics',
    type: 'decision',
    position: { x: 760, y: 120 },
    connections: ['inventive-principles', 'patterns-evolution', 'substance-field'],
    status: 'locked',
    details: 'Choose the most suitable TRIZ methodology: 40 Inventive Principles, Patterns of Evolution, Substance-Field Analysis, or Algorithm of Inventive Problem Solving (ARIZ).',
    nextSteps: ['Evaluate problem complexity', 'Match tool capabilities', 'Select primary methodology']
  },

  // Branch 1: 40 Inventive Principles
  {
    id: 'inventive-principles',
    name: '40 Inventive Principles',
    description: 'Apply the 40 fundamental principles for solving contradictions',
    type: 'ai',
    position: { x: 640, y: 280 },
    connections: ['principle-application'],
    status: 'locked',
    details: 'Use the contradiction matrix to identify applicable inventive principles from the 40 fundamental TRIZ principles for systematic problem solving.',
    nextSteps: ['Access contradiction matrix', 'Identify relevant principles', 'Generate solution concepts']
  },

  // Branch 2: Patterns of Evolution
  {
    id: 'patterns-evolution',
    name: 'Patterns of Evolution',
    description: 'Apply 8 patterns of technical system evolution',
    type: 'ai',
    position: { x: 880, y: 280 },
    connections: ['evolution-application'],
    status: 'locked',
    details: 'Use evolutionary trends to predict system development and identify innovative solutions based on natural progression patterns.',
    nextSteps: ['Analyze current evolution stage', 'Identify next evolution step', 'Design future system state']
  },

  // Branch 3: Substance-Field Analysis
  {
    id: 'substance-field',
    name: 'Substance-Field Analysis',
    description: 'Model the problem using substance-field methodology',
    type: 'ai',
    position: { x: 1120, y: 280 },
    connections: ['field-transformation'],
    status: 'locked',
    details: 'Create substance-field models to represent the problem structure and apply standard solutions for systematic innovation.',
    nextSteps: ['Build Su-Field model', 'Identify model type', 'Apply standard solutions']
  },

  // Phase 3: Solution Development
  {
    id: 'principle-application',
    name: 'Principle Application',
    description: 'Develop specific solutions using selected inventive principles',
    type: 'process',
    position: { x: 640, y: 440 },
    connections: ['solution-synthesis'],
    status: 'locked',
    details: 'Transform abstract inventive principles into concrete solution concepts tailored to your specific problem context.',
    nextSteps: ['Principle interpretation', 'Solution brainstorming', 'Concept development']
  },
  {
    id: 'evolution-application',
    name: 'Evolution Application',
    description: 'Develop solutions based on evolutionary patterns',
    type: 'process',
    position: { x: 880, y: 440 },
    connections: ['solution-synthesis'],
    status: 'locked',
    details: 'Create innovative solutions by applying evolutionary patterns and predicting the next development stage of the technical system.',
    nextSteps: ['Pattern application', 'Future state design', 'Innovation roadmap']
  },
  {
    id: 'field-transformation',
    name: 'Field Transformation',
    description: 'Transform the substance-field model to eliminate problems',
    type: 'process',
    position: { x: 1120, y: 440 },
    connections: ['solution-synthesis'],
    status: 'locked',
    details: 'Apply transformation rules to modify the substance-field model and create improved system configurations.',
    nextSteps: ['Model transformation', 'Standard solution application', 'System reconfiguration']
  },

  // Phase 4: Solution Synthesis & Evaluation
  {
    id: 'solution-synthesis',
    name: 'Solution Synthesis',
    description: 'Combine and refine all generated solution concepts',
    type: 'process',
    position: { x: 880, y: 600 },
    connections: ['solution-evaluation'],
    status: 'locked',
    details: 'Merge insights from different TRIZ tools, eliminate contradictions, and develop comprehensive solution concepts.',
    nextSteps: ['Concept integration', 'Contradiction resolution', 'Solution optimization']
  },
  {
    id: 'solution-evaluation',
    name: 'Solution Evaluation',
    description: 'Evaluate solutions against original problem requirements',
    type: 'decision',
    position: { x: 660, y: 760 },
    connections: ['implementation-planning'],
    status: 'locked',
    details: 'Assess solution feasibility, effectiveness, and potential side effects. Validate that contradictions are resolved without creating new problems.',
    nextSteps: ['Feasibility analysis', 'Risk assessment', 'Impact evaluation']
  },

  // Phase 5: Implementation
  {
    id: 'implementation-planning',
    name: 'Implementation Planning',
    description: 'Develop detailed implementation strategy and roadmap',
    type: 'process',
    position: { x: 440, y: 760 },
    connections: ['innovation-deployment'],
    status: 'locked',
    details: 'Create actionable implementation plans, identify required resources, and establish timeline for solution deployment.',
    nextSteps: ['Resource planning', 'Timeline development', 'Risk mitigation']
  },
  {
    id: 'innovation-deployment',
    name: 'Innovation Deployment',
    description: 'Deploy the innovative solution and monitor results',
    type: 'end',
    position: { x: 220, y: 760 },
    connections: [],
    status: 'locked',
    details: 'Execute the implementation plan, monitor performance, and establish feedback loops for continuous improvement.',
    nextSteps: ['Solution deployment', 'Performance monitoring', 'Continuous improvement']
  }
];

export const trizPrincipleBlocks = [
  {
    id: 'segmentation',
    name: 'Segmentation',
    principle: 1,
    description: 'Divide an object into independent parts',
    examples: ['Modular furniture', 'Sectional software', 'Microservices architecture'],
    applications: ['Easier handling', 'Independent operation', 'Scalability'],
    color: 'from-blue-400 to-blue-600'
  },
  {
    id: 'asymmetry',
    name: 'Asymmetry',
    principle: 2,
    description: 'Change the shape of an object from symmetrical to asymmetrical',
    examples: ['Asymmetric tire treads', 'Offset tools', 'Uneven load distribution'],
    applications: ['Better performance', 'Reduced wear', 'Improved efficiency'],
    color: 'from-green-400 to-green-600'
  },
  {
    id: 'local-quality',
    name: 'Local Quality',
    principle: 3,
    description: 'Change structure from uniform to non-uniform',
    examples: ['Variable thickness materials', 'Gradient properties', 'Localized treatments'],
    applications: ['Optimized performance', 'Material efficiency', 'Targeted functionality'],
    color: 'from-purple-400 to-purple-600'
  },
  {
    id: 'dynamics',
    name: 'Dynamics',
    principle: 15,
    description: 'Allow objects to adapt or be self-adjusting',
    examples: ['Adaptive interfaces', 'Self-adjusting systems', 'Dynamic optimization'],
    applications: ['Flexibility', 'Optimization', 'User adaptation'],
    color: 'from-orange-400 to-orange-600'
  },
  {
    id: 'feedback',
    name: 'Feedback',
    principle: 23,
    description: 'Introduce feedback to improve process or action',
    examples: ['Closed-loop control', 'User feedback systems', 'Performance monitoring'],
    applications: ['Process improvement', 'Quality control', 'System optimization'],
    color: 'from-pink-400 to-pink-600'
  },
  {
    id: 'self-service',
    name: 'Self-Service',
    principle: 25,
    description: 'Make objects serve themselves',
    examples: ['Self-healing materials', 'Autonomous systems', 'Self-diagnostics'],
    applications: ['Reduced maintenance', 'Automation', 'Reliability'],
    color: 'from-cyan-400 to-cyan-600'
  }
];

export const contradictionTypes = [
  {
    id: 'technical-contradiction',
    name: 'Technical Contradiction',
    description: 'Improving one parameter leads to degrading another',
    example: 'Increasing speed (good) leads to higher energy consumption (bad)',
    resolution: 'Use inventive principles to eliminate the contradiction',
    color: 'from-red-400 to-red-600'
  },
  {
    id: 'physical-contradiction',
    name: 'Physical Contradiction',
    description: 'One object must have opposite properties',
    example: 'Coffee should be hot (for taste) and cold (for summer)',
    resolution: 'Separate contradictory properties in space, time, or conditions',
    color: 'from-yellow-400 to-yellow-600'
  },
  {
    id: 'administrative-contradiction',
    name: 'Administrative Contradiction',
    description: 'The solution is known but cannot be implemented',
    example: 'We need more staff but cannot afford to hire',
    resolution: 'Find alternative resources or change the approach',
    color: 'from-indigo-400 to-indigo-600'
  }
];